import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorState } from './components/states/ErrorState';
import { LoadingState } from './components/states/LoadingState';
import { AuthProvider } from './context/AuthContext';
import { AuthGuard } from './components/auth/AuthGuard';
import { lazyWithRetry } from './utils/lazyWithRetry';
import './i18n';

const LoginPage = lazyWithRetry(() =>
  import('./components/LoginPage').then((module) => ({ default: module.LoginPage }))
);

const SessionListPage = lazyWithRetry(() =>
  import('./pages/SessionListPage').then((module) => ({ default: module.SessionListPage }))
);

const SessionTasksPage = lazyWithRetry(() =>
  import('./pages/SessionTasksPage').then((module) => ({ default: module.SessionTasksPage }))
);

const TaskViewerPage = lazyWithRetry(() =>
  import('./pages/TaskViewerPage').then((module) => ({ default: module.TaskViewerPage }))
);

const TaskReportPage = lazyWithRetry(() =>
  import('./pages/TaskReportPage').then((module) => ({ default: module.TaskReportPage }))
);

export const App = () => {
  return (
    <AuthProvider>
      <Suspense fallback={<LoadingState message="Loading portal..." />}>
        <ErrorBoundary fallback={<ErrorState title="Portal unavailable" message="Please refresh the page or contact support." />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/sessions"
              element={
                <AuthGuard>
                  <Layout>
                    <SessionListPage />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/sessions/:sessionId/tasks"
              element={
                <AuthGuard>
                  <Layout>
                    <SessionTasksPage />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/tasks/:taskId/viewer"
              element={
                <AuthGuard>
                  <TaskViewerPage />
                </AuthGuard>
              }
            />
            <Route
              path="/tasks/:taskId/report"
              element={
                <AuthGuard>
                  <Layout>
                    <TaskReportPage />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route path="/" element={<Navigate to="/sessions" replace />} />
            <Route path="*" element={<Navigate to="/sessions" replace />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
      <ToastContainer position="bottom-right" autoClose={4000} newestOnTop closeOnClick pauseOnHover={false} />
    </AuthProvider>
  );
};

export default App;
